// Where a signed-out user was heading before they were sent to log in.
//
// Auth0's own `returnTo` cannot carry this: every target would have to be
// registered in the tenant's Allowed Logout URLs. Keeping the decision in
// the app's router needs no tenant change at all - `SecurePage` records the
// attempted path, `AuthContext` sends the user there once they resolve.
//
// sessionStorage is deliberate: it is per-tab, so a link opened in a second
// tab cannot hijack navigation in the tab already open.
const STORAGE_KEY = 'pp.postLoginRedirect';

// Returning a user to one of these after login bounces them straight back
// out again - storing one is how this becomes a loop.
const EXCLUDED_PATHS = [
  '/login',
  '/unauthorized',
  '/email-not-verified',
  '/not-found',
];

const pathnameOf = (value: string) => {
  const path = value.split(/[?#]/)[0];
  // `/login/` and `/login` are the same route to the router.
  return path.length > 1 && path.endsWith('/')
    ? path.slice(0, -1)
    : path;
};

// This value decides where an authenticated user lands, so it has to be a
// same-origin path and nothing else: `//evil.example.com` and
// `/\evil.example.com` are both read as another origin by browsers. The type
// check is not redundant - this also vets whatever is sitting in storage,
// which the user can edit by hand.
const isSafeRedirectPath = (value: unknown): value is string =>
  typeof value === 'string' &&
  value.startsWith('/') &&
  !value.startsWith('//') &&
  !value.startsWith('/\\') &&
  !EXCLUDED_PATHS.includes(pathnameOf(value));

export const storePostLoginRedirect = (path: string) => {
  if (!isSafeRedirectPath(path)) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, path);
  } catch (error) {
    console.error('Error saving post-login redirect:', error);
  }
};

export const clearPostLoginRedirect = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing post-login redirect:', error);
  }
};

// For callers outside the router's render cycle. The axios interceptor is
// registered once, so react-router's `location` would be whatever it was at
// registration time; `window.location` is the live URL under BrowserRouter.
export const storeCurrentPostLoginRedirect = () =>
  storePostLoginRedirect(
    window.location.pathname +
      window.location.search +
      window.location.hash,
  );

// Reads and clears in one go: the path is good for exactly one login, and
// clearing before the caller navigates means a navigation that fails cannot
// replay it later.
export const consumePostLoginRedirect = (): string | null => {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error reading post-login redirect:', error);
    return null;
  }
  if (stored === null) return null;
  clearPostLoginRedirect();
  return isSafeRedirectPath(stored) ? stored : null;
};
